import type { FC, ReactNode } from 'react'
import type { Transaction } from '@/constants/dashboard'

import { cn } from '@/lib/utils'
import { formatCurrency, formatShortDate } from '@/lib/format'

interface TransactionsTableProps {
    transactions: Array<Transaction>
    compact?: boolean
    caption?: string
    className?: string
}

export const STATUS_STYLES: Record<Transaction['status'], string> = {
    Matched: 'bg-sage/20 text-sage-dark',
    Reconciled: 'bg-burgundy/[0.06] text-stone-dark',
    Review: 'bg-gold/25 text-gold-dark'
}

export const merchantInitials = (name: string): string => {
    return name
        .split(/\s+/)
        .filter(word => /^[A-Za-z]/.test(word))
        .slice(0, 2)
        .map(word => word[0].toUpperCase())
        .join('')
}

export const TransactionsTable: FC<TransactionsTableProps> = ({
    transactions,
    compact = false,
    caption = 'Recent transactions',
    className
}): ReactNode => {
    return (
        <div className={cn('-mx-1 overflow-hidden', className)}>
            <table className='w-full border-collapse text-left text-xs'>
                <caption className='sr-only'>{caption}</caption>

                <thead>
                    <tr className='text-[10px] uppercase tracking-[0.12em] text-stone'>
                        <th scope='col' className='px-1 pb-2 font-mono font-normal'>Merchant</th>
                        {!compact && <th scope='col' className='hidden px-1 pb-2 font-mono font-normal md:table-cell'>Date</th>}
                        <th scope='col' className='px-1 pb-2 text-right font-mono font-normal'>Amount</th>
                        <th scope='col' className='hidden px-1 pb-2 text-right font-mono font-normal sm:table-cell'>Status</th>
                    </tr>
                </thead>

                <tbody>
                    {transactions.map(transaction => (
                        <tr key={transaction.id} className='border-t border-burgundy/[0.06]'>
                            <td className='px-1 py-2.5'>
                                <div className='flex min-w-0 items-center gap-2.5'>
                                    <span
                                        aria-hidden='true'
                                        className='flex size-7 shrink-0 items-center justify-center rounded-full bg-cream text-[10px] font-medium text-burgundy'
                                    >
                                        {merchantInitials(transaction.merchant)}
                                    </span>

                                    <span className='min-w-0'>
                                        <span className='block truncate font-medium text-burgundy'>{transaction.merchant}</span>
                                        <span className='block truncate text-[11px] text-stone'>{transaction.category}</span>
                                    </span>
                                </div>
                            </td>

                            {!compact && (
                                <td className='hidden px-1 py-2.5 text-stone md:table-cell'>
                                    {formatShortDate(transaction.date)}
                                </td>
                            )}

                            <td
                                className={cn(
                                    'whitespace-nowrap px-1 py-2.5 text-right font-medium tabular-nums',
                                    transaction.amount > 0 ? 'text-positive' : 'text-burgundy'
                                )}
                            >
                                {transaction.amount > 0 ? '+' : '−'}
                                {formatCurrency(Math.abs(transaction.amount), true)}
                            </td>

                            <td className='hidden px-1 py-2.5 text-right sm:table-cell'>
                                <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium', STATUS_STYLES[transaction.status])}>
                                    {transaction.status === 'Review' ? 'Needs review' : transaction.status}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
